import Plausible from "plausible-tracker";
import MatomoTracker from "@jonkoops/matomo-tracker";
import { thirdPartyOk } from "./Cookie";

/**
 * @description Each of the provided analytics services must subscribe to two
 * events on the global observer:
 * analytics.trackPageView - takes no other parameters
 * analytics.trackEvent - requires one parameter, data. The parameter should have this type:
 *    {
 *      eventName: string,
 *      ...optionalProperties?: string | number
 *    }
 * Event name is required, while the remaining properties are optional, but could come
 * in handy when we want to provide additional payload to the event sent.
 *
 * @example
 * ```
 * // Track a page view
 * this.globalObserver.publish("analytics.trackPageView");
 *
 * // Track event named "customEvent", supply some payload
 * this.globalObserver.publish("analytics.trackEvent", {
 *  eventName: "customEvent",
 *  paramA: "foo",
 *  paramB: "bar"
 * });
 * ```
 *
 * @export
 * @class Analytics
 */
export default class Analytics {
  constructor(config, globalObserver) {
    switch (config?.type) {
      case "plausible":
        const { domain, apiHost, trackLocalhost } = config.options;
        const plausible = Plausible({
          domain,
          apiHost,
          trackLocalhost,
        });

        // These events should be called like this and subscribed to by
        // any analytics service. Below we define two events:
        globalObserver.subscribe("analytics.trackPageView", () =>
          plausible.trackPageview()
        );

        globalObserver.subscribe(
          "analytics.trackEvent",
          ({ eventName, ...rest }) => {
            plausible.trackEvent(eventName, { props: rest });
          }
        );
        break;
      case "matomo":
        let { siteId, trackerUrl } = config.options;

        const matomo = new MatomoTracker({
          urlBase: trackerUrl,
          siteId: siteId,
        });

        // Because of the nature of Matomo and how the tracking was implemented
        // we need to use a translate table to get the values we need.
        // If new events are added they will need to be added here as well.

        const eventValueKeys = {
          pluginShown: "pluginName",
          mapLoaded: "activeMap",
          layerShown: "layerName (layerId)", // concat the name and id.
          spatialSearchPerformed: "type",
          textualSearchPerformed: "query",
        };

        const regex = /[a-z0-0_].?[a-z0-9_]*/gim;

        const getValue = (eventName, data) => {
          // This gives us the possibility to merge values to one string, see layerShown
          const propNames = eventValueKeys[eventName];
          let value = propNames;
          let m;
          const repl = (match) => {
            value = value.replace(match, data[match]);
          };

          while ((m = regex.exec(propNames)) !== null) {
            if (m.index === regex.lastIndex) {
              regex.lastIndex++;
            }
            m.forEach(repl);
          }
          return value;
        };

        globalObserver.subscribe("analytics.trackPageView", () => {
          matomo.trackPageView();
        });

        globalObserver.subscribe(
          "analytics.trackEvent",
          ({ eventName, ...rest }) => {
            const value = getValue(eventName, rest);

            if (eventName.indexOf("SearchPerformed") > -1) {
              // spatial or textual
              matomo.trackSiteSearch({
                keyword: value,
                category: eventName,
                count: rest.totalHits, // always zero for spatial searches.
              });
              return;
            }

            // We send the retrieved value using the name prop which is predefined in Matomo.
            // The value prop is in useless in our cases as it only supports numbers.
            matomo.trackEvent({
              category: "general",
              action: eventName,
              name: value,
              value: 0,
            });
          }
        );
        break;
      case "vizzit":
        const { publicKey } = config.options;

        // TODO: Sweco SEMARA: Quick hack for SGI basic web stats.
        // Vizzit does not provide NPM or similar, only very old school static JS operating on multi-page expectancy DOM.
        // This will not work at all for an SPA like Hajk, so we need to adapt the old world to the new...
        // TODO: Clean this up, suggest an NPM to Vizzit? After cleanup, open issue + PR to Hajk. Check with SGI for maintaing this in core Hajk (highly recommended!).

        // State to track if Vizzit script has been loaded
        let vizzitScriptLoaded = false;

        // Store Vizzit config for deferred initialization
        const vizzitConfig = {
          keys: {
            public: publicKey || "",
          },
          config: {
            cookie: false,
            cookie_auto: false,
            tag: true,
            anonymize_ip: false,
            anonymize_username: false,
            username_tag: true,
            automatic_login: true,
            button_animation: true,
          },
        };

        // Helper function to initialize Vizzit
        // Only loads if user has consented to 3rd party cookies
        const initializeVizzit = () => {
          if (vizzitScriptLoaded || !thirdPartyOk()) {
            return; // Already loaded or user has not consented
          }

          // 1. Init $vizzit$ global with stored config
          window.$vizzit$ = vizzitConfig;

          // 2. Load static vizzit.integration.js (public/scripts/)
          // $vizzit$.integration.init() will load from CDN
          const vizzitScript = document.createElement("script");
          vizzitScript.src = "/scripts/vizzit.integration.js";
          vizzitScript.type = "text/javascript";
          document.head.appendChild(vizzitScript);

          vizzitScriptLoaded = true;
        };

        // 3. Subscribe to cookie consent changes
        // If user changes cookie consent, we need to know about it
        globalObserver.subscribe("core.cookieLevelChanged", () => {
          // Cookie level changed - reset flag to allow re-initialization if user consents
          if (!thirdPartyOk()) {
            vizzitScriptLoaded = false;
          }
        });

        // 4. Subscribe to Hajk analytic events and forward to Vizzit
        // a) Page views (pixel tracking)
        globalObserver.subscribe("analytics.trackPageView", () => {
          // Check cookie consent EVERY time we want to track
          if (!thirdPartyOk()) {
            // User has not consented to 3rd party cookies
            return;
          }

          // Initialize Vizzit if not already done
          if (!vizzitScriptLoaded) {
            initializeVizzit();
          }

          // Track current page after Vizzit is loaded
          if (
            window.$vizzit &&
            window.$vizzit.tag &&
            window.$vizzit.tag.socket
          ) {
            window.$vizzit.tag.socket.pixel(window.location.href);
          } else {
            // Retry if CDN script is not ready yet
            setTimeout(() => {
              if (
                window.$vizzit &&
                window.$vizzit.tag &&
                window.$vizzit.tag.socket
              ) {
                window.$vizzit.tag.socket.pixel(window.location.href);
              }
            }, 100);
          }
        });

        // TODO: Potential step 5: Subscript to Hajk analytics.trackEvent (layer click, map action, etc.) and forward to Vizzit as custom events.
        // globalObserver.subscribe(
        //   "analytics.trackEvent",
        //   ({ eventName, ...rest }) => {
        //     if (!thirdPartyOk()) return;
        //     if (
        //       window.$vizzit &&
        //       window.$vizzit.tag &&
        //       window.$vizzit.tag.socket
        //     ) {
        //       let eventUrl = `/vizzit-event/?event=${encodeURIComponent(
        //         eventName
        //       )}`;
        //       Object.entries(rest).forEach(([key, value]) => {
        //         eventUrl += `&${encodeURIComponent(key)}=${encodeURIComponent(
        //           String(value)
        //         )}`;
        //       });
        //       window.$vizzit.tag.socket.click(eventUrl);
        //     } else {
        //       setTimeout(() => {
        //         if (
        //           window.$vizzit &&
        //           window.$vizzit.tag &&
        //           window.$vizzit.tag.socket
        //         ) {
        //           let eventUrl = `/vizzit-event/?event=${encodeURIComponent(
        //             eventName
        //           )}`;
        //           Object.entries(rest).forEach(([key, value]) => {
        //             eventUrl += `&${encodeURIComponent(key)}=${encodeURIComponent(
        //               String(value)
        //             )}`;
        //           });
        //           window.$vizzit.tag.socket.click(eventUrl);
        //         }
        //       }, 100);
        //     }
        //   }
        // );
        break;

      default:
        break;
    }
  }
}
